<template>
  <div class="row mx-0">
    <div class="col-1"></div>
    <div class="col-10">
      <div class="p-0 m-0 mt-4 main-title">機台故障時間</div>
      <div style="margin-top: 2rem">
        <commonQueryInput
          :inputItems="multiInputItems"
          v-model:getValue="multiInputVal"
        ></commonQueryInput>
      </div>
      <div style="margin-top: 2rem">
        <queryResultTable
          :statisticalReports="true"
          :queryResultTableItems="queryResultTableItems"
        />
      </div>
      <div style="margin-top: 2rem">
        <chart
          :chartId="'machineFailTime-chart'"
          :styleGroup="{
            width: '100%',
            height: '250px',
            background: '#fff',
          }"
          :chartData="chartData"
          :chartType="'customize'"
          :exportChart="true"
          :exportChartName="exportChartName"
          :reDraw="true"
        />
      </div>
      <div class="text-end" style="margin-bottom: 0.6rem">
        <exportCsv
          :exportData="machineFailTimeItems"
          :exportDataTitle="machineFailTimeFields.map((item) => item.label)"
          :exportFileName="exportFileName()"
        />
      </div>
      <commonTable
        :fields="machineFailTimeFields"
        :items="machineFailTimeItems"
        :theme="'green'"
        stickyHeader="270px"
        style="margin-bottom: 2rem"
      >
      </commonTable>
    </div>
    <div class="col-1"></div>
  </div>
</template>

<script>
import commonQueryInput from "@/components/commonQueryInput.vue";
import chart from "@/components/chart.vue";
import queryResultTable from "@/components/queryResultTable.vue";
import stackBar from "@/components/stackBar.vue";
import exportCsv from "@/components/exportCsv.vue";
import commonTable from "@/components/commonTable.vue";
import { computed, inject, ref, watch } from "vue";
import { useStore } from "vuex";
export default {
  components: {
    commonQueryInput,
    chart,
    queryResultTable,
    stackBar,
    exportCsv,
    commonTable,
  },
  setup() {
    const store = useStore();

    //getters
    const loginData = computed(() => store.getters["getLogin/get_loginData"]);
    const sideNavPath = computed(
      () => store.getters["sideBarTree/get_sideNavPath"]
    );
    const axiosResult = computed(
      () => store.getters["commonAxios/get_axiosResult"]
    );
    const oper = computed(() => store.getters["selectOptions/get_oper"]);
    const device = computed(() => store.getters["selectOptions/get_device"]);

    //dispatch
    const methods = {
      axiosAction: async (payload) => {
        await store.dispatch("commonAxios/axiosAction", payload);
      },
      triggerLoadingBar: () => {
        store.dispatch("commonLoadingBar/triggerLoadingBar");
      },
      setTimeoutAlert: (alertText) => {
        store.dispatch("commonAlert/setTimeoutAlert", [alertText]);
      },
    };

    const currentYear = new Date().getFullYear();
    const yearOptins = ref([]);

    const path = inject("path");
    const multiInputVal = ref();
    const multiInputItems = ref([
      {
        key: "year",
        label: "年度",
        checkEmpty: true,
        type: "select", // date/dateBetween/text/select/time/month
        checkEmpty: true,
        disableText: "-- 選擇年度 --",
        options: yearOptins,
        disableValue: currentYear,
      },
      {
        key: "month",
        label: "月份",
        type: "singleMonth",
      },
      {
        key: "oper",
        label: "站別",
        type: "select",
        checkEmpty: true,
        disableText: "-- 選擇站別 --",
        options: oper,
      },
      {
        key: "deviceID",
        label: "機台",
        type: "select",
        bindKey: "oper",
        checkEmpty: true,
        disableText: "-- 選擇機台 --",
        bindOptions: device,
      },
    ]);

    //查詢回來的機台名稱
    const queryDate = ref({
      year: "",
      month: "",
      site: "",
      oper: "",
      device: "",
    });

    //查詢結果表格資料
    const queryResultTableItems = ref([]);

    //圖表名稱
    const exportChartName = ref("chart");
    //圖表資料
    const chartData = ref({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      grid: {
        top: "13%",
        left: "5%",
        right: "4%",
        bottom: "5%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          data: [],
          // splitLine: { show: false },
          axisPointer: {
            type: "shadow",
          },
          axisLabel: {
            interval: 0, // 強制顯示所有標籤
            rotate: 45, // 旋轉45度以適應空間
          },
        },
      ],
      yAxis: {
        type: "value",
        name: "時數(hr)",
        minInterval: 1,
        scale: true,
      },
      series: [
        {
          name: "時間",
          label: {
            // show: true,
            position: "top",
          },
          itemStyle: {
            color: "#4b8c80",
          },
          data: [],
          type: "bar",
        },
      ],
    });

    //機台故障時間資訊
    const machineFailTimeFields = [
      {
        key: "date",
        label: "日期",
        sortable: true,
        thStyle: {
          "min-width": "80px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
      {
        key: "hour",
        label: "故障時數(hr)",
        sortable: true,
        thStyle: {
          "min-width": "110px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
    ];
    const machineFailTimeItems = ref([]);

    //匯出資料的檔案名稱
    const exportFileName = () => {
      let exportFileName = "";
      if (queryDate.value.year != "") {
        exportFileName =
          "機台故障時間_" +
          queryDate.value.year +
          "-" +
          (queryDate.value.month == "-" ? "" : queryDate.value.month + "-") +
          (queryDate.value.oper == "-" ? "" : queryDate.value.oper + "-") +
          (queryDate.value.device == "-" ? "" : queryDate.value.device + "-");
      }
      return exportFileName;
    };

    const getYearOptins = () => {
      for (let year = currentYear; year >= currentYear - 5; year--) {
        yearOptins.value.push({
          value: year,
          text: year,
        });
      }
    };

    //watch 按查詢鍵回傳所有輸入框的值
    watch(
      multiInputVal,
      (newValue) => {
        methods.triggerLoadingBar();
        let params = {};
        params["webPage"] =
          sideNavPath.value.length != 0
            ? sideNavPath.value[sideNavPath.value.length - 1]
            : null;
        params["user"] = loginData.value.account;
        params["vuePage"] = path.value;
        console.log(path.value);
        params["methods"] = "POST";
        params["whichFunction"] = "machineFailTime";
        params["year"] = newValue["year"];
        newValue["month"] != undefined
          ? (params["month"] = newValue["month"])
          : "";
        params["oper"] = newValue["oper"];
        newValue["deviceID"] != undefined
          ? (params["deviceID"] = newValue["deviceID"])
          : "";

        methods.axiosAction(params).then(() => {
          console.log(axiosResult.value);
          let result = axiosResult.value;
          let response = result.Response;
          if (response == "ok") {
            let queryTableData = result.QueryTableData;
            queryDate.value = {
              year: queryTableData.queryDate[0],
              month: queryTableData.queryDate[1],
              site: queryTableData.queryDate[2],
              oper: queryTableData.queryDate[3],
              device: queryTableData.queryDate[4],
            };
            queryResultTableItems.value = [
              {
                year: queryDate.value.year,
                month: queryDate.value.month,
                site: queryDate.value.site,
                oper: queryDate.value.oper,
                device: queryDate.value.device,
              },
            ];
            exportChartName.value =
              "機台故障時間_" +
              queryDate.value.year +
              "-" +
              (queryDate.value.month == "-"
                ? ""
                : queryDate.value.month + "-") +
              (queryDate.value.oper == "-" ? "" : queryDate.value.oper + "-") +
              (queryDate.value.device == "-"
                ? ""
                : queryDate.value.device + "-") +
              "chart";
            machineFailTimeItems.value = queryTableData.machineFailTimeItems;
            chartData.value.xAxis[0].data = queryTableData.chartData.xAxis;
            chartData.value.series[0].data = queryTableData.chartData.data;
          } else {
            queryDate.value = {
              year: "",
              month: "",
              site: "",
              oper: "",
              device: "",
            };
            queryResultTableItems.value = [];
            exportChartName.value = null;
            machineFailTimeItems.value = [];
            methods.setTimeoutAlert(response);
          }
          methods.triggerLoadingBar();
        });
      },
      { deep: true }
    );

    getYearOptins();
    return {
      multiInputVal,
      multiInputItems,
      queryResultTableItems,
      chartData,
      exportChartName,
      machineFailTimeFields,
      machineFailTimeItems,
      exportFileName,
    };
  },
};
</script>

<style></style>
